import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, switchMap, tap, filter, take, map } from 'rxjs/operators';
import { environment } from '@env/environment';
import { UserDataService } from './../data-services/user-data.service';
import { StorageServiceObservablesService } from './../storage-service-observables/storage-service-observables.service';
import { BankAccountService } from './bank-account.service';
import { PolicyDataService } from '../data-services/policy-data.service';
import { ActionWidgetService } from '@services/widget/action-widget.service';

@Injectable({
  providedIn: 'root',
})
export class UserDetailsService {
  primaryPhoneType: string;

  constructor(
    private http: HttpClient,
    private bankAccountService: BankAccountService,
    private storageServiceObservablesService: StorageServiceObservablesService,
    private userDataService: UserDataService,
    private policyDataService: PolicyDataService,
    private actionWidgetService: ActionWidgetService,
  ) {}

  getUserDetailsByEmail(): Observable<any> {
    const email = this.storageServiceObservablesService.getUserFromStorage();
    const url = `${environment.backend_server_url_identity}/${email}`;

    return this.http.get(url).pipe(
      tap((userResponse: any) => {
        // Emit name immediately — home page and header render right away
        this.userDataService.updateUserDetails({ userDetails: userResponse });
        // Bank accounts load in the background and update $userData when ready
        this.loadBankAccountsInBackground(userResponse, email);
      })
    );
  }

  private loadBankAccountsInBackground(userResponse: any, email: string): void {
    const existing = this.policyDataService.getPolicyList();
    const policies$ = (existing != null)
      ? this.policyDataService.$policyDetails.pipe(
          filter((p) => p !== null && p.length > 0),
          take(1)
        )
      : this.policyDataService.$rawPolicies.pipe(
          filter((p) => p !== null),
          take(1)
        );

    policies$.pipe(
      switchMap((policies: any[]) => {
        if (!policies?.length) return of(null);

        const userPhoneList = this.setUserPhoneList(policies[0]);
        // forkJoin fires all calls in parallel and preserves index order
        const bankCalls = policies.map((policy) =>
          this.bankAccountService
            .getBankAccountByEmailWithPolicyNumber(email, policy.policynumber.policyNumber)
            .pipe(catchError(() => of(false)))
        );

        return forkJoin(bankCalls).pipe(
          tap((results: any[]) => {
            const userBankDetails = results.map((result, i) => {
              if (result) {
                result.policyNumber = policies[i]?.policynumber.policyNumber ?? '';
                return result;
              }
              return [];
            });
            this.userDataService.updateUserDetails({
              userDetails: { ...userResponse, phoneList: userPhoneList },
              bankAccountDetails: userBankDetails,
            });
          })
        );
      })
    ).subscribe();
  }

  public setCustomActionWidget(): Observable<any>{
    const email = this.storageServiceObservablesService.getUserFromStorage();
    const url = `${environment.backend_server_url_account}/custom-action-widgets/${email.toLowerCase()}`;
      return this.http.get(url, { responseType: 'text' }).pipe(
        tap(res => {
          if(res){
            this.actionWidgetService.convertAndSet(res);
          }
        })
      );
  }
  
  isPrimaryPhone(type) {
    return type === this.primaryPhoneType;    
  }
  setUserPhoneList(policy) {
    const insuredToGetPhoneDetialsFrom =
      policy && policy.insured.find((insuredElm) => insuredElm.displayPhone);
    if (insuredToGetPhoneDetialsFrom) {
      this.primaryPhoneType = insuredToGetPhoneDetialsFrom.primaryPhoneType;
      return [
        {
          number: insuredToGetPhoneDetialsFrom.mobilePhone,
          type: 'cell',
          primary: this.isPrimaryPhone('MOBILE'),
        },
        {
          number: insuredToGetPhoneDetialsFrom.homePhone,
          type: 'home',
          primary: this.isPrimaryPhone('HOME'),
        },
        {
          number: insuredToGetPhoneDetialsFrom.workPhone,
          type: 'work',
          primary: this.isPrimaryPhone('WORK'),
        },
      ];
    }
    return [];
  }
  getUserForCookie() {
    const email = this.storageServiceObservablesService.getUserFromStorage();
    const url = `${environment.backend_server_url_identity}/${email}`;

    return this.http.get(url).pipe(
      map((response: any) => {
        return `${response.firstName} ${response.lastName}`;
      })
    );
  }

  getUserDataByEmail(token, email) {
    const url = `${environment.backend_server_url_identity_guest}/getUserWithToken`;
    const body = {
      email,
      password: '',
      token,
      newPassword: '',
    };
    return this.http.post(url, body).pipe(
      map((response: any) => {
        return response;
      })
    );
  }
}
